import type { CSSProperties } from "react";
import type { Ambient, DesignTokens, Font, MotionStyle, ResolvedTheme, Texture } from "@baze/catalog";

/**
 * The part of a ResolvedTheme the renderer needs. Pass a full `ResolvedTheme` (from
 * `resolveTheme()` in @baze/catalog) or `null` to render with the neutral fallback theme.
 */
export type EngineTheme = Pick<ResolvedTheme, "tokens" | "fonts"> & {
  design?: Pick<ResolvedTheme["design"], "id" | "variants">;
};

const FALLBACK_FONT: Font = {
  id: "plus-jakarta-sans",
  family: "Plus Jakarta Sans Variable",
  package: "@fontsource-variable/plus-jakarta-sans",
  category: "sans",
  fallback: "ui-sans-serif, system-ui, sans-serif",
};

/** Neutral theme used when the definition references a design missing from the catalog. */
export const FALLBACK_THEME: EngineTheme = {
  tokens: {
    colors: {
      primary: "#1D4ED8",
      onPrimary: "#FFFFFF",
      secondary: "#93C5FD",
      accent: "#0EA5E9",
      background: "#FFFFFF",
      surface: "#F8FAFC",
      text: "#0F172A",
      muted: "#475569",
      border: "#E2E8F0",
      inverted: "#0F172A",
      onInverted: "#F8FAFC",
    },
    radius: { sm: "0.375rem", md: "0.75rem", lg: "1.25rem", button: "999px" },
    shadow: {
      sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
      md: "0 8px 24px rgba(15, 23, 42, 0.08)",
      lg: "0 24px 60px rgba(15, 23, 42, 0.12)",
    },
    spacing: "normal",
    heading: { weight: 700, case: "normal", tracking: "-0.02em" },
  },
  fonts: { heading: FALLBACK_FONT, body: FALLBACK_FONT },
};

/** Section rhythm per spacing token: [mobile, ≥768px] block padding and the heading→content gap. */
const SPACING: Record<DesignTokens["spacing"], { y: string; yLg: string; gap: string; gapLg: string }> = {
  compact: { y: "3rem", yLg: "4.5rem", gap: "2rem", gapLg: "2.5rem" },
  normal: { y: "4rem", yLg: "6rem", gap: "2.5rem", gapLg: "3.5rem" },
  airy: { y: "5rem", yLg: "7.5rem", gap: "2.75rem", gapLg: "4rem" },
};

// Defense in depth: tokens come from the compiled catalog (validated) and user overrides
// (validated hex). Anything unexpected is replaced with the fallback, never interpolated raw.
const HEX = /^#[0-9a-fA-F]{6}$/;
const LENGTH = /^(?:0|[0-9]*\.?[0-9]+(?:rem|px|em))$/;
const SHADOW = /^[0-9a-zA-Z.,()#%\s-]{1,200}$/;
const TRACKING = /^-?[0-9]*\.?[0-9]+em$|^0$/;

const pick = (value: string | undefined, re: RegExp, fallback: string) =>
  value && re.test(value) ? value : fallback;

export function fontStack(
  font: Pick<Font, "family" | "fallback"> | undefined,
  fallback = FALLBACK_FONT,
): string {
  const f = font ?? fallback;
  const family = f.family.replace(/[^A-Za-z0-9 -]/g, "").trim() || fallback.family;
  const rest = f.fallback.replace(/[^A-Za-z0-9 ,"-]/g, "").trim();
  return rest ? `"${family}", ${rest}` : `"${family}"`;
}

/** CSS custom properties for a theme. Set inline on the site root by `<SiteRoot>`. */
export function themeVariables(theme: EngineTheme | null | undefined): Record<string, string> {
  const t = theme ?? FALLBACK_THEME;
  const fb = FALLBACK_THEME.tokens;
  const c = t.tokens.colors;
  const color = (k: keyof DesignTokens["colors"]) => pick(c[k], HEX, fb.colors[k]);
  const spacing = SPACING[t.tokens.spacing] ?? SPACING.normal;
  const weight = Number.isInteger(t.tokens.heading.weight)
    ? Math.min(900, Math.max(300, t.tokens.heading.weight))
    : 700;
  return {
    "--site-primary": color("primary"),
    "--site-on-primary": color("onPrimary"),
    "--site-secondary": color("secondary"),
    "--site-accent": color("accent"),
    "--site-bg": color("background"),
    "--site-surface": color("surface"),
    "--site-text": color("text"),
    "--site-muted": color("muted"),
    "--site-border": color("border"),
    "--site-inverted": color("inverted"),
    "--site-on-inverted": color("onInverted"),
    // Page-level copies used by section tones to swap bg/surface without self-reference.
    "--site-page-bg": color("background"),
    "--site-page-surface": color("surface"),
    "--site-radius-sm": pick(t.tokens.radius.sm, LENGTH, fb.radius.sm),
    "--site-radius-md": pick(t.tokens.radius.md, LENGTH, fb.radius.md),
    "--site-radius-lg": pick(t.tokens.radius.lg, LENGTH, fb.radius.lg),
    "--site-radius-button": pick(t.tokens.radius.button, LENGTH, fb.radius.button),
    "--site-shadow-sm": pick(t.tokens.shadow.sm, SHADOW, fb.shadow.sm),
    "--site-shadow-md": pick(t.tokens.shadow.md, SHADOW, fb.shadow.md),
    "--site-shadow-lg": pick(t.tokens.shadow.lg, SHADOW, fb.shadow.lg),
    "--site-font-heading": fontStack(t.fonts.heading),
    "--site-font-body": fontStack(t.fonts.body),
    "--site-heading-weight": String(weight),
    "--site-heading-case": t.tokens.heading.case === "uppercase" ? "uppercase" : "none",
    "--site-heading-tracking": pick(t.tokens.heading.tracking, TRACKING, "0"),
    "--site-section-y": spacing.y,
    "--site-section-y-lg": spacing.yLg,
    "--site-gap": spacing.gap,
    "--site-gap-lg": spacing.gapLg,
  };
}

// Kept in sync with MOTION_STYLES in @baze/catalog (a value import there would pull zod into public sites).
const MOTION_STYLES: readonly MotionStyle[] = [
  "subtle",
  "cinematic",
  "playful",
  "editorial",
  "dreamy",
  "bold",
  "kinetic",
  "parallax",
  "paper",
  "retro",
  "celestial",
  "pixel",
];
const TEXTURES: readonly Texture[] = [
  "none",
  "grain",
  "grid",
  "dots",
  "aurora",
  "paper",
  "halftone",
  "night",
];
const AMBIENTS: readonly Ambient[] = ["petals", "snow", "stars", "fireflies", "bubbles", "none"];

/** The theme's background texture; anything unknown is "none". */
export function textureOf(theme: EngineTheme | null | undefined): Texture {
  const t = theme?.tokens.texture;
  return t && TEXTURES.includes(t) ? t : "none";
}

/** Particles over invitation covers; anything unknown (or unset) is "petals". */
export function ambientOf(theme: EngineTheme | null | undefined): Ambient {
  const a = theme?.tokens.ambient;
  return a && AMBIENTS.includes(a) ? a : "petals";
}

/** The theme's motion style; anything unknown falls back to the engine default ("subtle"). */
export function motionStyle(theme: EngineTheme | null | undefined): MotionStyle {
  const m = theme?.tokens.motion;
  return m && MOTION_STYLES.includes(m) ? m : "subtle";
}

export function themeStyle(theme: EngineTheme | null | undefined): CSSProperties {
  return themeVariables(theme) as CSSProperties;
}
